/**
 * Loader for signalsmith-stretch (MIT, WASM + AudioWorklet).
 *
 * The library builds its worklet by stringifying its own functions, which a minifier breaks (the stringified
 * body references renamed identifiers). So the untouched ESM build is served from /worklets/signalsmith-stretch.js
 * and imported at runtime with a vite-ignored dynamic import. Works on both AudioContext and OfflineAudioContext.
 */

export interface StretchSchedule {
  active?: boolean;
  /** position in the input buffer (seconds) */
  input?: number;
  /** audio-context time of this change (seconds) */
  output?: number;
  rate?: number;
  semitones?: number;
  tonalityHz?: number;
  formantSemitones?: number;
  formantCompensation?: boolean;
  formantBaseHz?: number;
  loopStart?: number;
  loopEnd?: number;
}

export interface StretchNode extends AudioWorkletNode {
  inputTime: number;
  start(when?: number | StretchSchedule, offset?: number, duration?: number, rate?: number, semitones?: number): Promise<unknown>;
  stop(when?: number): Promise<unknown>;
  schedule(s: StretchSchedule, adjustPrevious?: boolean): Promise<unknown>;
  addBuffers(buffers: Float32Array[]): Promise<number>;
  dropBuffers(toSeconds?: number): Promise<unknown>;
  latency(): Promise<number>;
  configure(c: { blockMs?: number | null; intervalMs?: number; splitComputation?: boolean; preset?: 'default' | 'cheaper' }): Promise<unknown>;
  setUpdateInterval(seconds: number, cb?: (t: number) => void): Promise<unknown>;
}

export type StretchFactory = (ctx: BaseAudioContext, options?: AudioWorkletNodeOptions) => Promise<StretchNode>;

export const BASE_URL: string = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || '/';
const STRETCH_URL = `${BASE_URL}worklets/signalsmith-stretch.js`;

let factoryPromise: Promise<StretchFactory> | null = null;
let available: boolean | null = null;

/** Resolve the SignalsmithStretch factory (cached). Rejects when the library cannot be loaded. */
export function loadStretch(): Promise<StretchFactory> {
  if (!factoryPromise) {
    // Dev: Vite refuses to import files from /public, so use the package (the dep optimizer does not minify, which
    // keeps the library's self-stringified worklet intact). Prod: the untouched ESM copy served from /worklets.
    type Mod = { default?: StretchFactory } & Record<string, unknown>;
    const source: Promise<Mod> = import.meta.env.DEV
      ? (import('signalsmith-stretch') as Promise<Mod>)
      : (import(/* @vite-ignore */ STRETCH_URL) as Promise<Mod>);
    factoryPromise = source
      .then((m: Mod) => {
        const f = (m.default ?? (m as unknown as { SignalsmithStretch?: StretchFactory }).SignalsmithStretch) as StretchFactory | undefined;
        if (typeof f !== 'function') throw new Error('signalsmith-stretch: no factory export');
        available = true;
        return f;
      })
      .catch((e: unknown) => {
        available = false;
        factoryPromise = null;
        throw e;
      });
  }
  return factoryPromise;
}

export const stretchAvailable = (): boolean | null => available;

/** Race a promise against a timeout (worklet handshakes can hang silently in broken environments). */
export function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(`${what}: timed out after ${ms} ms`)), ms);
    p.then((v) => { clearTimeout(id); resolve(v); }, (e) => { clearTimeout(id); reject(e); });
  });
}
